import Button from '@mui/material/Button';
import Image from 'next/image';
import Link from 'next/link';
import { Trans } from 'react-i18next';
import {
  HomeAdventureTourAction,
  HomeAdventureTourBody,
  HomeAdventureTourCard,
  HomeAdventureTourDescription,
  HomeAdventureTourImageLink,
  HomeAdventureTourTitle,
} from './style';
import { getThumbnailImage } from '@/utils/utils';

export type HomeTourItem = {
  id: string;
  slug: string;
  title: string;
  shortContent: string;
  imageDetails?: Array<{ url?: string; alt?: string }>;
  thumbnailImage?: string;
  tourType?: string;
};

type TourCardProps = {
  tour: HomeTourItem;
  lang: string;
  buttonBgColor?: string;
  buttonHoverBgColor?: string;
};

const safeShortContent = (value: string): string => {
  if (!value) return '';
  return value
    .replace(/<\s*br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const TourCard = ({
  tour,
  lang,
  buttonBgColor = '#A55F1A',
  buttonHoverBgColor = '#8F5115',
}: TourCardProps) => {
  const sanitizedShortContent = safeShortContent(tour.shortContent);
  const thumbnailImage = getThumbnailImage(tour as any) || '/assets/images/ic_app_logo.png';

  return (
    <HomeAdventureTourCard>
      <Link href={`/${lang}/${tour.tourType}/${tour.slug}`} passHref legacyBehavior>
        <HomeAdventureTourImageLink aria-label={`View details for ${tour.title}`}>
          <Image
            src={thumbnailImage}
            alt={tour.title}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            style={{
              objectFit: 'cover',
              borderRadius: '0.5rem',
              transition: 'transform 500ms ease',
            }}
          />
        </HomeAdventureTourImageLink>
      </Link>
      <HomeAdventureTourBody>
        <Link
          href={`/${lang}/${tour.tourType}/${tour.slug}`}
          style={{ textDecoration: 'none', flex: 1, display: 'block' }}
          aria-label={`View details for ${tour.title}`}
        >
          <HomeAdventureTourTitle>{tour.title}</HomeAdventureTourTitle>
          <HomeAdventureTourDescription>{sanitizedShortContent}</HomeAdventureTourDescription>
        </Link>
        <HomeAdventureTourAction>
          <Link
            href={`/${lang}/${tour.tourType}/${tour.slug}`}
            tabIndex={0}
            aria-label={`Book now for ${tour.title}`}
            style={{ textDecoration: 'none', display: 'inline-flex' }}
          >
            <Button
              size="large"
              sx={{
                backgroundColor: buttonBgColor,
                color: '#fff',
                fontWeight: 700,
                minWidth: 'unset',
                px: 2,
                py: 0.75,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: buttonHoverBgColor,
                },
              }}
            >
              <Trans i18nKey="home.bookNow" defaults="BOOK NOW" />
            </Button>
          </Link>
        </HomeAdventureTourAction>
      </HomeAdventureTourBody>
    </HomeAdventureTourCard>
  );
};

export default TourCard;
